import { Sparkles, X } from "lucide-react";
import { CHANGELOG, type ChangelogAudience } from "../data/changelog.ts";
import { useAuthStore } from "../store/useAuthStore.ts";

interface Props {
  onClose: () => void;
}

/** Which audiences a role should see. Higher roles see everything below them. */
function visibleAudiences(role: string | undefined): Set<ChangelogAudience> {
  if (role === "ADMIN" || role === "COORDINATOR") {
    return new Set<ChangelogAudience>(["all", "reviewer", "coordinator"]);
  }
  if (role === "REVIEWER") return new Set<ChangelogAudience>(["all", "reviewer"]);
  return new Set<ChangelogAudience>(["all"]);
}

export default function ChangelogModal({ onClose }: Props) {
  const user = useAuthStore((s) => s.user);
  const audiences = visibleAudiences(user?.role);

  const releases = CHANGELOG.map((release) => ({
    ...release,
    entries: release.entries.filter((e) => audiences.has(e.audience)),
  })).filter((release) => release.entries.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg bg-card border rounded-xl shadow-xl flex flex-col max-h-[85vh]">
        <div className="flex items-center gap-3 p-5 border-b shrink-0">
          <Sparkles className="h-5 w-5 text-primary shrink-0" />
          <h2 className="font-semibold text-lg flex-1">What&apos;s new</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-6">
          {releases.map((release) => (
            <div key={release.version}>
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-primary/10 text-primary">
                  v{release.version}
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(release.date).toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
              </div>
              <ul className="space-y-2">
                {release.entries.map((entry, i) => (
                  <li
                    key={i}
                    className="text-sm text-muted-foreground leading-relaxed pl-3 border-l-2 border-border"
                  >
                    {entry.text}
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {releases.length === 0 && (
            <p className="text-sm text-muted-foreground italic">
              No release notes to show yet.
            </p>
          )}
        </div>

        <div className="p-4 border-t shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2 px-4 border rounded-md text-sm font-medium hover:bg-accent"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
